import type React from 'react'
import { useState, useMemo } from 'react'
import { Switch, Popover, message } from 'antd'
import { Horizontal, Vertical, GraviGrid, Texto, GraviButton } from '@gravitate-js/excalibrr'
import {
  type TerminalMappingsResponse,
  useSupplierReconciliation,
  type TerminalMappings,
} from '@api/useSupplierReconciliation'
import { useMasterMetadata } from '@api/useMetadata/useMasterMetadata'
import type { SupplierConfigMetadataResponse } from '@api/useMetadata/types'
import { getTerminalMappingsColumns } from './columnDefs'
import { AddExternalValuePopover } from '../shared/AddExternalValuePopover'
import { PlusOutlined } from '@ant-design/icons'
import { gridViewManager } from '@/utils/grid'
import { useUserSettings } from '@/hooks/settings'

const STORAGE_KEY = 'terminal-mappings-grid'

export function TerminalMappingTab() {
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false)
  const [isAddingExternalTerminal, setIsAddingExternalTerminal] = useState(false)

  const { updateSetting } = useUserSettings()

  // Get terminal mapping data and supplier config data
  const { useTerminalMappingsOverview, useUpsertTerminalMapping, useConfigOverview } = useSupplierReconciliation()
  const { data: mappingData, isLoading: isMappingLoading } = useTerminalMappingsOverview()
  const { data: configData } = useConfigOverview()

  // Get metadata options
  const { data: metadata } = useMasterMetadata<SupplierConfigMetadataResponse>('supplier_config')

  // Get update mutation
  const { mutateAsync: upsertTerminalMapping } = useUpsertTerminalMapping()

  // Handle adding new external terminal
  async function handleAddExternalTerminal(values: {
    source_name: string
    source_id?: string
    counterparty_id?: string
    supplier_id?: string
    location_ids?: string[]
  }) {
    // Find existing mapping for this source_name and supplier_id
    const existingMapping = mappingData?.find(
      (item: TerminalMappings) => item.source_name === values.source_name && item.supplier_id === values.supplier_id
    )

    // Get existing terminal IDs
    let existingTerminalIds: string[] = []
    if (existingMapping?.terminals && Array.isArray(existingMapping.terminals)) {
      existingTerminalIds = existingMapping.terminals.map((t: { terminal_id: string }) => t.terminal_id).filter(Boolean)
    }

    // Get new terminal IDs
    const newTerminalIds = values.location_ids || []

    // Combine existing and new terminal IDs (remove duplicates)
    const combinedTerminalIds = [...new Set([...existingTerminalIds, ...newTerminalIds])]

    try {
      message.loading('Adding external terminal...', 0)
      await upsertTerminalMapping({
        body: {
          source_name: values.source_name,
          supplier_id: values.supplier_id,
          terminal_ids: combinedTerminalIds,
        },
      })
      message.destroy()
      message.success('External terminal added successfully')
    } catch (error) {
      message.destroy()
      console.error('Failed to add terminal:', error)
      message.error('Failed to add external terminal')
    } finally {
      setIsAddingExternalTerminal(false)
    }
  }

  // Get column definitions
  const columnDefs = useMemo(() => getTerminalMappingsColumns(), [])

  async function updateMapping(data: TerminalMappingsResponse['rows'][number]) {
    let terminalIds: string[] = []

    // Check if this is edited data from the grid (will have terminal_names array)
    if (data.terminal_names && Array.isArray(data.terminal_names)) {
      // Convert terminal names to IDs
      terminalIds = data.terminal_names
        .map((name) => metadata?.locations.find((location) => location.name === name)?.id)
        .filter(Boolean) as string[]
    }
    // Otherwise, it's original data from API with terminals array
    else if (data.terminals && Array.isArray(data.terminals)) {
      terminalIds = data.terminals.map((t: { terminal_id: string }) => t.terminal_id).filter(Boolean) as string[]
    }

    try {
      message.loading('Updating terminal mapping...', 0)
      await upsertTerminalMapping({
        body: {
          supplier_id: data.supplier_id,
          source_name: data.source_name,
          terminal_ids: terminalIds,
        },
      })
      message.destroy()
      message.success('Terminal mapping updated successfully')
    } catch (error) {
      message.destroy()
      console.error('Failed to update terminal mapping:', error)
      message.error('Failed to update terminal mapping')
    }
  }

  const filteredItems = useMemo(() => {
    if (!mappingData) return undefined
    if (showUnmappedOnly) {
      return mappingData.filter((item: TerminalMappings) => {
        // Check all possible data structures for unmapped items
        if (Array.isArray(item.terminals) && item.terminals.length > 0) {
          return false // Has mappings
        }
        if (Array.isArray(item.terminal_names) && item.terminal_names.length > 0) {
          return false // Has mappings
        }
        if (item.terminal_name) {
          return false // Has mapping
        }
        return true // No mappings found
      })
    }
    return mappingData
  }, [mappingData, showUnmappedOnly])

  return (
    <Vertical gap={16}>
      <GraviGrid
        rowData={filteredItems}
        columnDefs={columnDefs}
        storageKey={STORAGE_KEY}
        loading={isMappingLoading}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
        controlBarProps={{
          title: 'Terminal Mappings',
          actionButtons: (
            <Horizontal gap={8} alignItems='center'>
              <Texto>Show Unmapped Only</Texto>
              <Switch
                data-testid='show-unmapped-only-switch'
                checked={showUnmappedOnly}
                onChange={setShowUnmappedOnly}
              />
              <Popover
                trigger='click'
                placement='bottomRight'
                open={isAddingExternalTerminal}
                onOpenChange={setIsAddingExternalTerminal}
                content={
                  <AddExternalValuePopover
                    type='terminal'
                    onSubmit={handleAddExternalTerminal}
                    supplierConfigs={configData?.rows}
                    terminalOptions={(metadata?.locations || []).sort(
                      (a, b) => a.name?.localeCompare(b.name || '') || 0
                    )}
                  />
                }
              >
                <GraviButton icon={<PlusOutlined />} buttonText='Add External Terminal' theme2 />
              </Popover>
            </Horizontal>
          ),
          rowCount: filteredItems?.length || 0,
        }}
        agPropOverrides={{
          rowSelection: 'single',
          groupDefaultExpanded: -1,
          getRowId: (params) => {
            const baseId = params.data.source_name + params.data.supplier_id
            // Create unique ID by including all mapped terminal IDs
            if (Array.isArray(params.data.terminals) && params.data.terminals.length > 0) {
              const terminalIds = params.data.terminals
                .map((t: { terminal_id: string }) => t.terminal_id)
                .sort()
                .join(',')
              return baseId + '_terminals_' + terminalIds
            }
            // Fallback for unmapped items
            return baseId + '_unmapped'
          },
          context: {
            terminalOptions: metadata?.locations || [],
          },
          onCellEditingStopped: (params) => {
            // Only trigger API call if value actually changed
            if (!params.valueChanged) return

            // Use oldValue and newValue directly from AG Grid for accurate comparison
            const normalizeValue = (value: unknown): string[] => {
              if (!value) return []
              if (Array.isArray(value)) {
                return value.filter((name) => name && name !== 'No Mapping').sort()
              }
              if (typeof value === 'string' && value !== 'No Mapping') {
                return [value]
              }
              return []
            }

            const oldValues = normalizeValue(params.oldValue)
            const newValues = normalizeValue(params.newValue)

            // Deep comparison: check if arrays are actually different
            const arraysEqual =
              oldValues.length === newValues.length && oldValues.every((name, index) => name === newValues[index])

            if (arraysEqual) {
              console.log('Terminal mapping: No real change detected', { oldValues, newValues })
              return // No real change in terminal mappings
            }

            console.log('Terminal mapping: Change detected, updating...', { oldValues, newValues })
            updateMapping(params.data)
          },
        }}
      />
    </Vertical>
  )
}
